import { ConnectionState, DisconnectReason, isE2EESupported, Room, RoomEvent, Track, } from 'livekit-client';
import { RTC_PROTOCOL_V1 } from '../protocol/constants.js';
import { RtcError, silentLogger, toRtcError } from '../protocol/types.js';
import { TypedEmitter } from '../util/emitter.js';
export const disconnectReasonName = (reason) => {
    if (reason === undefined || reason === null) {
        return DisconnectReason[DisconnectReason.UNKNOWN_REASON];
    }
    return typeof reason === 'number' && typeof DisconnectReason[reason] === 'string' ? DisconnectReason[reason] : String(reason);
};
const textOf = (value) => (typeof value === 'string' && value.length > 0 ? value : undefined);
const scaled = (value, factor) => typeof value === 'number' && Number.isFinite(value) ? Math.round(value * factor) : undefined;
export const summarizeTransportStats = (report) => {
    const entries = [];
    report.forEach(entry => entries.push(entry));
    const byId = new Map(entries.map(entry => [entry.id, entry]));
    const selectedPairId = entries.find(entry => entry.type === 'transport' && textOf(entry.selectedCandidatePairId))?.selectedCandidatePairId;
    const pair = (typeof selectedPairId === 'string' ? byId.get(selectedPairId) : undefined) ??
        entries.find(entry => entry.type === 'candidate-pair' && entry.state === 'succeeded' && entry.nominated === true);
    const local = typeof pair?.localCandidateId === 'string' ? byId.get(pair.localCandidateId) : undefined;
    const pairType = textOf(local?.candidateType);
    const remoteInbound = entries.find(entry => entry.type === 'remote-inbound-rtp');
    return {
        pairType,
        transport: (pairType === 'relay' ? textOf(local?.relayProtocol) : undefined) ?? textOf(local?.protocol),
        rttMs: scaled(pair?.currentRoundTripTime, 1000),
        outBitrateKbps: scaled(pair?.availableOutgoingBitrate, 0.001),
        lossPct: scaled(remoteInbound?.fractionLost, 100),
        jitterMs: scaled(remoteInbound?.jitter, 1000),
    };
};
export const isMediaEncryptionSupported = () => {
    try {
        return isE2EESupported();
    }
    catch {
        return false;
    }
};
const QUALITY_LEVELS = ['excellent', 'good', 'poor', 'lost', 'unknown'];
const toQuality = (value) => QUALITY_LEVELS.includes(value) ? value : 'unknown';
const SNAPSHOT_EVENTS = [
    RoomEvent.ParticipantConnected,
    RoomEvent.ParticipantDisconnected,
    RoomEvent.TrackPublished,
    RoomEvent.TrackUnpublished,
    RoomEvent.TrackSubscribed,
    RoomEvent.TrackUnsubscribed,
    RoomEvent.TrackMuted,
    RoomEvent.TrackUnmuted,
    RoomEvent.LocalTrackPublished,
    RoomEvent.LocalTrackUnpublished,
    RoomEvent.ActiveSpeakersChanged,
    RoomEvent.ConnectionQualityChanged,
    RoomEvent.ParticipantEncryptionStatusChanged,
];
const toRtcConfiguration = ({ iceServers, relayOnly }) => {
    if (!iceServers || iceServers.length === 0) {
        return undefined;
    }
    return {
        iceServers: iceServers.map(server => ({ urls: [...server.urls], username: server.username, credential: server.credential })),
        ...(relayOnly ? { iceTransportPolicy: 'relay' } : {}),
    };
};
export const defaultRoomOptions = () => ({
    adaptiveStream: true,
    dynacast: true,
    disconnectOnPageLeave: true,
    publishDefaults: {
        simulcast: true,
        videoCodec: 'vp8',
        dtx: true,
        red: true,
    },
});
export class LivekitController {
    constructor(options) {
        this.emitter = new TypedEmitter();
        this.unsubscribers = [];
        this.intentionalDisconnect = false;
        this.logger = options.logger ?? silentLogger;
        const roomOptions = {
            ...defaultRoomOptions(),
            ...options.roomOptions,
            encryption: options.e2ee,
        };
        this.room = options.roomFactory ? options.roomFactory(roomOptions) : new Room(roomOptions);
        this.bind();
    }
    async connect(url, jwt, options = {}) {
        this.intentionalDisconnect = false;
        const startedAt = Date.now();
        const rtcConfig = toRtcConfiguration(options);
        const turn = rtcConfig !== undefined;
        const relayOnly = rtcConfig?.iceTransportPolicy === 'relay';
        try {
            await this.room.setE2EEEnabled(true);
            await this.room.connect(url, jwt, { autoSubscribe: true, ...(rtcConfig ? { rtcConfig } : {}) });
        }
        catch (error) {
            this.logger.warn('rtc.media.connect_failed', {
                connectMs: Date.now() - startedAt,
                turn,
                relayOnly,
                error: error instanceof Error ? error.message : String(error),
            });
            throw toRtcError(error, 'media_failed');
        }
        this.logger.info('rtc.media.connected', { connectMs: Date.now() - startedAt, turn, relayOnly });
        if (!this.room.isE2EEEnabled) {
            await this.room.disconnect(true);
            throw new RtcError('not_encrypted', 'End-to-end encryption could not be enabled for media');
        }
    }
    async disconnect() {
        this.intentionalDisconnect = true;
        await this.room.disconnect(true);
    }
    get connectionState() {
        switch (this.room.state) {
            case ConnectionState.Connected:
                return 'connected';
            case ConnectionState.Connecting:
                return 'connecting';
            case ConnectionState.Reconnecting:
            case ConnectionState.SignalReconnecting:
                return 'reconnecting';
            default:
                return 'disconnected';
        }
    }
    get localIdentity() {
        return this.room.localParticipant?.identity || undefined;
    }
    get participantSid() {
        return this.room.localParticipant?.sid || undefined;
    }
    async healthSample() {
        const quality = toQuality(this.room.localParticipant?.connectionQuality);
        const track = [...(this.room.localParticipant?.trackPublications.values() ?? [])]
            .map(publication => publication.track)
            .find(candidate => candidate !== undefined);
        const report = await track?.getRTCStatsReport().catch(() => undefined);
        return report ? { quality, ...summarizeTransportStats(report) } : { quality };
    }
    async setMicrophone(enabled, options) {
        try {
            await this.room.localParticipant.setMicrophoneEnabled(enabled, options);
        }
        catch (error) {
            throw toRtcError(error, 'media_failed');
        }
    }
    async setCamera(enabled, options) {
        try {
            await this.room.localParticipant.setCameraEnabled(enabled, options);
        }
        catch (error) {
            throw toRtcError(error, 'media_failed');
        }
    }
    async setScreenShare(enabled, options) {
        try {
            await this.room.localParticipant.setScreenShareEnabled(enabled, options);
        }
        catch (error) {
            throw toRtcError(error, 'media_failed');
        }
    }
    async switchDevice(kind, deviceId) {
        try {
            return await this.room.switchActiveDevice(kind, deviceId);
        }
        catch (error) {
            throw toRtcError(error, 'media_failed');
        }
    }
    async publishSignal(payload) {
        await this.room.localParticipant.publishData(payload, {
            reliable: true,
            topic: RTC_PROTOCOL_V1.signalTopic,
        });
    }
    snapshot() {
        const participants = [];
        if (this.room.localParticipant?.identity) {
            participants.push(this.room.localParticipant);
        }
        this.room.remoteParticipants.forEach(participant => participants.push(participant));
        return participants.map(participant => this.toSnapshot(participant));
    }
    on(event, listener) {
        return this.emitter.on(event, listener);
    }
    dispose() {
        this.unsubscribers.forEach(unsubscribe => unsubscribe());
        this.unsubscribers.length = 0;
        this.emitter.removeAll();
    }
    toSnapshot(participant) {
        const isLocal = participant === this.room.localParticipant;
        return {
            identity: participant.identity,
            isLocal,
            speaking: participant.isSpeaking,
            audioLevel: participant.audioLevel,
            microphoneEnabled: participant.isMicrophoneEnabled,
            cameraEnabled: participant.isCameraEnabled,
            screenSharing: participant.isScreenShareEnabled,
            connectionQuality: toQuality(participant.connectionQuality),
            encrypted: this.isEncrypted(participant),
        };
    }
    isEncrypted(participant) {
        const publications = [...participant.trackPublications.values()].filter(publication => publication.source !== Track.Source.Unknown);
        if (publications.length === 0) {
            return true;
        }
        return participant.isEncrypted;
    }
    bind() {
        const changed = () => this.emitter.emit('changed');
        SNAPSHOT_EVENTS.forEach(event => {
            this.room.on(event, changed);
            this.unsubscribers.push(() => this.room.off(event, changed));
        });
        this.listen(RoomEvent.ConnectionStateChanged, () => this.emitter.emit('connection', this.connectionState));
        this.listen(RoomEvent.SignalReconnecting, () => {
            this.reconnectingSince ?? (this.reconnectingSince = Date.now());
            this.logger.info('rtc.media.signal_reconnecting');
        });
        this.listen(RoomEvent.Reconnecting, () => {
            this.reconnectingSince ?? (this.reconnectingSince = Date.now());
            this.logger.warn('rtc.media.reconnecting');
            this.emitter.emit('connection', 'reconnecting');
        });
        this.listen(RoomEvent.Reconnected, () => {
            const durationMs = this.reconnectingSince === undefined ? undefined : Date.now() - this.reconnectingSince;
            this.reconnectingSince = undefined;
            this.logger.info('rtc.media.reconnected', { durationMs });
            this.emitter.emit('connection', 'connected');
        });
        this.listen(RoomEvent.Disconnected, (reason) => {
            this.reconnectingSince = undefined;
            const unexpected = !this.intentionalDisconnect;
            this.logger.info('rtc.media.disconnected', { unexpected, reason: disconnectReasonName(reason) });
            this.emitter.emit('disconnected', unexpected, reason);
        });
        this.listen(RoomEvent.DataReceived, (payload, participant, _kind, topic) => {
            if (topic === RTC_PROTOCOL_V1.signalTopic) {
                this.emitter.emit('data', payload, participant?.identity);
            }
        });
        this.listen(RoomEvent.EncryptionError, (error, participant) => {
            this.emitter.emit('encryptionError', error, participant?.identity);
        });
        this.listen(RoomEvent.MediaDevicesError, (error, kind) => {
            this.emitter.emit('deviceError', error, kind);
        });
    }
    listen(event, listener) {
        const handler = listener;
        this.room.on(event, handler);
        this.unsubscribers.push(() => this.room.off(event, handler));
    }
}
